import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  exhaustMap,
  filter,
  interval,
  map,
  merge,
  mergeMap,
  of,
  startWith,
  switchMap,
  take,
  takeUntil
} from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { nutritionGuideActions } from '../nutrition-guide/nutrition-guide.action';
import { selectProcessGenerationNourishPlus } from '../nutrition-guide/nutrition-guide.selectors';
import { ReportNourishGuidelineService } from '../nutrition-guide/nutrition-guide.service';
import { recalculateActions } from './recalculate.actions';
import {
  BaseResponseBody,
  NutritionGuidelineRecap
} from './recalculate.interface';
import { ReportNourishRecalculateService } from './recalculate.service';

/**
 * Report Nourish Recalculate Effects.
 */
@Injectable()
export class RecalculateEffects extends ReportNourishRecalculateService {
  private readonly actions$ = inject(Actions);
  private readonly nourishGuidelineService = inject(
    ReportNourishGuidelineService
  );
  private readonly store = inject(Store);

  /**
   * Fetch recap effect.
   * If NOT_FOUND error occurs, dispatch generate recap action.
   */
  fetchRecap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recalculateActions.fetchRecap),
      switchMap(({ nutritionGuidelineId }) =>
        this.getRecap({ nutritionGuidelineId }).pipe(
          switchMap((response: BaseResponseBody<NutritionGuidelineRecap>) => {
            if (!response.data) {
              return of(
                // If no data, dispatch generate recap action
                recalculateActions.generateRecap({ nutritionGuidelineId })
              );
            }

            return of(
              recalculateActions.fetchRecapSuccess({
                recap: response.data
              })
            );
          }),
          catchError((error: any) => {
            // Check if error is NOT_FOUND
            if (error?.error === 'NOT_FOUND') {
              return of(
                recalculateActions.fetchRecapNotFound({ nutritionGuidelineId })
              );
            }
            return of(
              recalculateActions.fetchRecapFailure({
                error: error?.message ?? 'Failed to fetch recap'
              })
            );
          })
        )
      )
    )
  );

  generateNourishPlusExecute$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recalculateActions.generationNourishPlusExecute),
      switchMap(() =>
        this.nourishGuidelineService.generationNourishPlusExecute().pipe(
          take(1),
          map(() => recalculateActions.generationNourishPlusExecuteSuccess()),
          catchError((error) =>
            of(
              recalculateActions.generationNourishPlusExecuteFailure({
                error:
                  error?.message ?? 'Failed to execute Nourish Plus generation'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Generate recap effect.
   */
  generateRecap$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recalculateActions.generateRecap),
      switchMap(({ nutritionGuidelineId }) =>
        this.generate({ nutritionGuidelineId }).pipe(
          map((response: BaseResponseBody<NutritionGuidelineRecap>) =>
            recalculateActions.generateRecapSuccess({
              recap: response.data as NutritionGuidelineRecap
            })
          ),
          catchError((error: any) =>
            of(
              recalculateActions.generateRecapFailure({
                error: error?.message ?? 'Failed to generate recap'
              })
            )
          )
        )
      )
    )
  );

  /**
   * Auto-generate recap when not found.
   */
  generateRecapOnNotFound$ = createEffect(() =>
    this.actions$.pipe(
      ofType(recalculateActions.fetchRecapNotFound),
      map(({ nutritionGuidelineId }) =>
        recalculateActions.generateRecap({ nutritionGuidelineId })
      )
    )
  );

  /**
   * Initiate subscription to nutrigenetic plus guideline updated process effect.
   */
  initiateSubscriptionNutrigeneticPlusProcess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(recalculateActions.initiateSubscriptionNutrigeneticPlusProcess),
        exhaustMap(() =>
          this.nutrigeneticPlusGuidelineUpdatedProcess().pipe(
            mergeMap((response) => {
              return of(
                recalculateActions.initiateSubscriptionNutrigeneticPlusProcessSuccess(
                  { response }
                )
              );
            }),
            catchError((error: any) =>
              of(
                recalculateActions.initiateSubscriptionNutrigeneticPlusProcessFailure(
                  {
                    error:
                      error?.message ??
                      'Failed to initiate subscription process'
                  }
                )
              )
            )
          )
        )
      ),
    {
      dispatch: true
    }
  );

  intervalNutrigeneticPlusProcess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(recalculateActions.nutrigeneticPlusProcessIntervalCheckerStart),
        exhaustMap(() =>
          interval(1000 * 15).pipe(
            startWith(0),
            // Stop conditions merged into ONE stream
            takeUntil(
              merge(
                // Stop when history length <= 0
                this.store
                  .select(selectProcessGenerationNourishPlus)
                  .pipe(filter((process) => process?.history.length === 0)),

                // Stop when isFailedGenerate === true
                this.store
                  .select(selectProcessGenerationNourishPlus)
                  .pipe(
                    filter((process) => process?.isFailedGenerate === true)
                  ),

                // Stop when completionPercentage === 100
                this.store
                  .select(selectProcessGenerationNourishPlus)
                  .pipe(
                    filter((process) => process?.completionPercentage === 100)
                  ),

                // Stop when END action dispatched
                this.actions$.pipe(
                  ofType(
                    recalculateActions.nutrigeneticPlusProcessIntervalCheckerEnd
                  )
                )
              )
            ),
            map(() =>
              nutritionGuideActions.fetchProcessGenerationNourishPlus({
                isWaitingProgressCompleted: true
              })
            )
          )
        )
      ),
    {
      dispatch: true
    }
  );

  onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => recalculateActions.reset())
      );
    },
    { dispatch: true }
  );
}
